import type { AxisSelection, IExploreCountry } from '@atlas/types'
import { AxisCode, BRAND_KIT_FILE_TYPES, ClassifiedObjectType, FileType, type RuleElementType } from '@atlas/types'
import { useQueries, useQuery } from '@tanstack/react-query'
import type { Message } from '@/domain/message.types'
import type { Tagged } from '@/lib/axis-applicability'
import { partitionAxisSelection } from '@/lib/axis-selection'
import { classificationApi } from '@/services/classification.service'
import { exploreApi } from '@/services/explore.service'
import { type FileWithTagsDTO, fileService } from '@/services/file.service'
import { messageApi } from '@/services/message.service'
import { rulesApi } from '@/services/rules.service'
import { useAttributeAxisCodes } from './useLibrary'

/** React-query hooks for the Explore browser + Brand and Product pages. */

export const useRegions = () => useQuery({ queryKey: ['explore', 'regions'], queryFn: exploreApi.regions })

/** Shared key so a single-region read and the whole-catalog read hit the same cache entry. */
const countriesKey = (regionCode: string) => ['explore', 'countries', regionCode]

export const useRegionCountries = (regionCode: string | null) =>
	useQuery({
		queryKey: countriesKey(regionCode as string),
		queryFn: () => exploreApi.countries(regionCode as string),
		enabled: !!regionCode,
	})

/**
 * Every browsable market at once: the regions plus the countries of each region that has
 * any. Explore lists brands before a region is picked, and the read model exposes no flat
 * brand endpoint, so the per-region country reads are fanned out and recombined here.
 *
 * Regions with `countryCount === 0` are skipped - they would cost a request to learn what
 * their own rollup already said. Each read is cached under the same key
 * `useRegionCountries` uses, so drilling in after landing costs nothing.
 */
export function useExploreCatalog() {
	const { data: regions = [], isLoading: loadingRegions, isError: regionsError } = useRegions()
	const browsable = regions.filter((r) => r.countryCount > 0)

	const results = useQueries({
		queries: browsable.map((r) => ({ queryKey: countriesKey(r.code), queryFn: () => exploreApi.countries(r.code) })),
	})

	const countriesByRegion: Record<string, IExploreCountry[]> = {}
	browsable.forEach((region, i) => {
		countriesByRegion[region.code] = results[i]?.data ?? []
	})

	return {
		regions,
		countriesByRegion,
		isLoading: loadingRegions || results.some((r) => r.isLoading),
		isError: regionsError || results.some((r) => r.isError),
	}
}

/**
 * Distinct assets per brand across the CURRENT scope, as one scope-wide read.
 *
 * Not derivable from `useExploreCatalog`: that fans out per region, so a brand's rows there each
 * count within one region only, and a file tagged `global` appears in every one of them. Summing
 * double-counts it and picking one region under-reports — the union only exists server side.
 *
 * A country selection still reads its REGION's totals: the card reports the brand across the scope
 * being browsed, and narrowing to one market does not change how many files the brand has.
 */
export const useBrandAssetTotals = (regionCode: string | null) =>
	useQuery({
		queryKey: ['brand-totals', regionCode],
		queryFn: () => exploreApi.brandTotals(regionCode),
	})

/** The market is part of the key, so adding or clearing it refetches the page. */
export const useBrandPage = (brandId: string | null, market?: string | null) =>
	useQuery({
		queryKey: ['brand-page', brandId, market ?? null],
		queryFn: () => exploreApi.brandPage(brandId as string, market),
		enabled: !!brandId,
	})

/**
 * The subject a coverage context is about, as opposed to the tuple narrowing it — the
 * identity that has to match for the previous answer to still be worth showing.
 */
// One axis, not two: a brand and a product are both `brand`-axis codes, so the subject is
// that axis's selection — which already distinguishes a brand page from a product page.
const subjectOf = (context: AxisSelection): string => JSON.stringify(context[AxisCode.BRAND] ?? null)

/**
 * Rules covering a context. Kept as its own query rather than folded into the brand
 * page: rules live in rule-service, which already depends on classification-service,
 * so composing them server-side would close a dependency cycle (ADR-0010). It also
 * means a slow coverage read costs the card a spinner, not the whole page.
 *
 * Narrowing the tuple keeps the previous coverage on screen while the next resolves — the
 * caller dims it — because emptying the card for a round trip reads as "no rules apply"
 * rather than "still asking". Only within the same subject: another product's coverage
 * standing in for this one would be a wrong answer, not a stale one.
 */
export const useApplicableRules = (context: AxisSelection, enabled = true, elementType?: RuleElementType) =>
	useQuery({
		// `elementType` is part of the key: a surface resolving only text rules must not read
		// the unfiltered result another surface cached for the same tuple.
		queryKey: ['rules', 'applicable', context, elementType ?? null],
		queryFn: () => rulesApi.applicable(context, undefined, elementType),
		enabled,
		placeholderData: (previous, previousQuery) =>
			previousQuery && subjectOf(previousQuery.queryKey[2] as AxisSelection) === subjectOf(context)
				? previous
				: undefined,
	})

/**
 * The geography scope is part of the key, so resolving for another region or market
 * refetches — the server counts and validates the code, none of which the client can derive.
 *
 * The previous scope's page stays on screen while the next one resolves, so narrowing does
 * not blank the whole page for a round-trip. Only for the SAME product: on navigation to
 * another one, holding the last product's identity would be a lie, not a transition.
 */
export const useProductPage = (productId: string | null, market?: string | null) =>
	useQuery({
		queryKey: ['product-page', productId, market ?? null],
		queryFn: () => exploreApi.productPage(productId as string, market),
		enabled: !!productId,
		placeholderData: (previous) => (previous?.product.productId === productId ? previous : undefined),
	})

/**
 * The brands and products of the catalogue — what the product page's hierarchy picker
 * steps sideways through. Read whole and cached under one key each, because both lists are
 * the same for every page and the picker needs a brand's products before that brand's page
 * has ever been opened.
 */
export const useBrands = () => useQuery({ queryKey: ['brands'], queryFn: classificationApi.listBrands })

export const useProducts = () => useQuery({ queryKey: ['products'], queryFn: classificationApi.listProducts })

/**
 * The product's DELIVERED assets for the resolved geography, narrowed by the page's own
 * filters — the same discovery read the Library browse runs on, so both surfaces answer
 * "which assets" one way. Kept out of the page read because file metadata lives in
 * file-service, which already depends on classification-service (ADR-0010).
 *
 * `file_type: deliverable` belongs to THIS read rather than to the caller's selection: a
 * product carries source files and its brand's kit on the same axis, and an unconstrained
 * read would count those as delivered and list a brand-kit file twice on one page. Applied
 * after the rail's filters, so a `file_type` pick — which the rail does offer, and which
 * narrows the brand kit beside this grid — can never widen what counts as delivered.
 *
 * Returns each file with its tags, so the grid derives its channel pills and
 * aspect-ratio labels from the assets themselves rather than a parallel grouping.
 *
 * `railSelection` is multi-valued per axis (OR within an axis, AND across them) because it
 * comes from the filter rail, not from the page's resolution — the read accepts exactly
 * that shape, so no axis needs collapsing to one value on the way in. The rail offers
 * attribute axes (`aspect_ratio`) next to dimensions, so the selection is split into
 * discovery's two channels on the way out; `product` and `geography` are dimensions and
 * always ride in `filters`.
 *
 * The previous read's assets stay on screen while the next one runs — the caller dims them
 * — so narrowing costs the grid a dimming rather than a full skeleton. Only for the SAME
 * product: another product's assets would be a wrong answer, not a stale one.
 */
/**
 * A geography-scoped discovery read: BOTH closure directions, unioned and deduped by file id.
 *
 * Geography is a tree (`global` → region → market), and neither direction alone answers "this
 * scope's assets" at every level of it:
 *
 *   - `filed_under` matches at or BELOW the scope, so a region picks up its markets' artwork but
 *     misses the `global`/region-level files that genuinely apply there.
 *   - `applies_in` matches at or ABOVE it, so a market picks up the wider artwork but a region
 *     misses everything filed in its own markets.
 *
 * A sibling market is neither an ancestor nor a descendant, so the union never widens across the
 * tree — `emea_uk` stays out of `emea_es`. What it must never become is a browse over the scope's
 * whole LADDER: every market descends from `global`, so that matches the entire library.
 *
 * Unscoped reads pass no `match` and run once, exactly as before.
 */
const discoverAcrossScope = async (
	filters: AxisSelection,
	attributeFilters: AxisSelection,
	scoped: boolean,
): Promise<FileWithTagsDTO[]> => {
	const files: FileWithTagsDTO[] = []
	const matches: (undefined | 'applies_in' | 'filed_under')[] = scoped ? ['applies_in', 'filed_under'] : [undefined]
	for (const match of matches) {
		// Bounded rather than open-ended: a paging bug on either side would otherwise spend
		// requests forever, and no scope is plausibly past this many files.
		for (let page = 1; page <= MAX_DISCOVER_PAGES; page++) {
			const batch = await fileService.discover(filters, attributeFilters, DISCOVER_PAGE_SIZE, page, match)
			files.push(...batch)
			if (batch.length < DISCOVER_PAGE_SIZE) break
		}
	}
	// A file tagged exactly AT the scope satisfies both directions (the closure includes the self
	// row), so it comes back from each read.
	return [...new Map(files.map((f) => [f.fileId, f])).values()]
}

export const useProductAssets = (
	productCode: string | null,
	market?: string | null,
	railSelection: AxisSelection = {},
) => {
	const attributeAxisCodes = useAttributeAxisCodes()
	const { filters, attributeFilters } = partitionAxisSelection(railSelection, attributeAxisCodes)
	const dimensionFilters: AxisSelection = {
		[AxisCode.BRAND]: [productCode as string],
		...(market ? { [AxisCode.GEOGRAPHY]: [market] } : {}),
		...filters,
		[AxisCode.FILE_TYPE]: [FileType.DELIVERABLE],
	}
	return useQuery({
		queryKey: ['product-assets', productCode, market ?? null, filters, attributeFilters],
		// Scope-aware when a market is set: a product's `global` deliverable is as real in NORAM as
		// one filed at `noram_us`, and a filed-under-only read dropped it.
		queryFn: () => discoverAcrossScope(dimensionFilters, attributeFilters, !!market),
		enabled: !!productCode,
		placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === productCode ? previous : undefined),
	})
}

/**
 * The brand's brand-kit files — those carrying a brand-kit `file_type`. `file_type` is a
 * dimension axis, so it belongs in `filters`: `attributeFilters` matches object_attribute
 * rows, which a file_type tag never writes.
 */
export const useBrandKitFiles = (brandCode: string | null) =>
	useQuery({
		queryKey: ['brand-kit-files', brandCode],
		queryFn: () =>
			fileService.discover({
				[AxisCode.BRAND]: [brandCode as string],
				[AxisCode.FILE_TYPE]: [...BRAND_KIT_FILE_TYPES],
			}),
		enabled: !!brandCode,
	})

/**
 * Every file classified to the brand, whatever role it plays - the Brand page's Identity tab lists
 * the brand's whole inventory rather than only its kit, so no `file_type` narrows this read.
 *
 * Geography narrows it only when the page is scoped, and then by BOTH closure directions unioned:
 *
 *   - COVERAGE (`applies_in`) — files tagged AT OR ABOVE the scope. A logo tagged `emea`, or
 *     worldwide, is valid in Spain because the wider scope covers the market.
 *   - FILED-UNDER (`filed_under`) — files tagged AT OR BELOW it. Scoping to NORAM must include the
 *     artwork filed at `noram_us`, because a region is a container of markets, not a place artwork
 *     applies TO.
 *
 * Coverage alone was right only for a leaf market. The geography tree is `global` → region →
 * market, so asking coverage for a REGION matched just `{global, noram}` and dropped every file
 * filed in that region's own markets — NORAM read 1 asset where the Explore grid, which counts
 * over a region's countries, read 4.
 *
 * The union stays correct at leaf level too: a sibling market is neither an ancestor nor a
 * descendant of `emea_es`, so `emea_uk` is still excluded. What it can never be is a browse over
 * the scope's whole ladder — every market descends from `global`, so that matches the library.
 *
 * Read page by page because discovery hard-caps a page at 100 (`file.service.ts`): a brand-wide
 * read with no `file_type` filter passes that routinely, and stopping at the first page would
 * report a partial inventory as the whole of it.
 */
export const useBrandFiles = (brandCode: string | null, marketCode: string | null = null) =>
	useQuery({
		queryKey: ['brand-files', brandCode, marketCode],
		queryFn: async (): Promise<FileWithTagsDTO[]> => {
			const filters: AxisSelection = { [AxisCode.BRAND]: [brandCode as string] }
			if (marketCode) filters[AxisCode.GEOGRAPHY] = [marketCode]
			return discoverAcrossScope(filters, {}, !!marketCode)
		},
		enabled: !!brandCode,
	})

/** The largest page `POST /files/discover` accepts; asking for more is a 422. */
const DISCOVER_PAGE_SIZE = 100
const MAX_DISCOVER_PAGES = 20

/**
 * The brand's logo assets — the hero resolves its image from these.
 *
 * A LOGO IS AN ASSET, NOT A COLUMN. `brand.logo_ref` exists and is empty on every row; rather
 * than maintain a second home for the same fact, the hero asks the same question the Identity
 * tab does — "what is classified `file_type=logo` to this brand" — so a logo uploaded by hand
 * and a seeded one travel one path, and replacing a logo is an upload rather than a schema edit.
 *
 * Its own narrow read rather than a slice of {@link useBrandFiles}: that read is unfiltered and
 * pages up to 2000 files, and the hero is above the tabs, so reusing it would pull the brand's
 * whole inventory on page arrival — exactly what the Identity tab's own read is scoped to avoid.
 * `file_type=logo` is typically one or two files, so this is a single small request.
 *
 * A brand with several logos (regional wordmarks — Bravecto ships a US and a UK one) resolves the
 * FIRST, because the page holds no applicability ladder to choose between them by market. Stating
 * one is better than showing several or none; the Identity tab lists them all.
 */
export const useBrandLogos = (brandCode: string | null) =>
	useQuery({
		queryKey: ['brand-logos', brandCode],
		queryFn: (): Promise<FileWithTagsDTO[]> =>
			fileService.discover({
				[AxisCode.BRAND]: [brandCode as string],
				[AxisCode.FILE_TYPE]: [FileType.LOGO],
			}),
		enabled: !!brandCode,
	})

/** A product message with the tags the page narrows it by. */
export type MessageWithTags = Message & Tagged

/**
 * The messages behind a set of ids, each with its own tags.
 *
 * Two steps because the message list endpoint filters by message fields, not by classification:
 * the batch tag read says what each one is classified to, and each is hydrated for the type,
 * name, locale and copy the Texts card states. The last step is a per-id fan-out - one request
 * per text.
 *
 * That fan-out is windowed and failure-tolerant, because the brand page feeds it a list no
 * single subject bounds: the brand's own copy PLUS every product's, across every locale.
 * Unwindowed it would open one connection per text at once; all-or-nothing, a single text
 * deleted between the search and the hydrate would replace the whole card with an error
 * instead of the texts that did resolve. An id that fails is dropped from the result.
 *
 * An empty id list stops here: the batch reads take a NON-EMPTY list (`objectIds: []` is a 422),
 * and there is nothing to ask about anyway.
 */
const HYDRATE_WINDOW = 6

async function hydrateMessages(ids: string[]): Promise<MessageWithTags[]> {
	if (ids.length === 0) return []
	const [tagsByObject, messages] = await Promise.all([
		classificationApi.getObjectTags(ClassifiedObjectType.MESSAGE, ids),
		hydrateInWindows(ids),
	])
	const tagsOf = new Map(tagsByObject.map((o) => [o.objectId, o.tags]))
	return messages.map((message) => ({ ...message, tags: tagsOf.get(message.messageId) ?? [] }))
}

/** The per-id reads, at most `HYDRATE_WINDOW` in flight, skipping the ones that fail. */
async function hydrateInWindows(ids: string[]): Promise<Message[]> {
	const messages: Message[] = []
	for (let start = 0; start < ids.length; start += HYDRATE_WINDOW) {
		const window = await Promise.allSettled(ids.slice(start, start + HYDRATE_WINDOW).map((id) => messageApi.get(id)))
		for (const outcome of window) {
			if (outcome.status === 'fulfilled') messages.push(outcome.value)
		}
	}
	return messages
}

/**
 * The messages a product page chooses from: everything classified under the product's BRAND,
 * each with its own tags. The search resolves the ids; {@link hydrateMessages} turns them into
 * the texts the card states.
 *
 * Read brand-wide and narrowed by the caller ({@link narrowToApplicable}) on the product's
 * brand path, because a classification filter matches a value's DESCENDANTS: a read for the
 * product alone would never reach copy tagged for the brand above it. The same narrowing drops
 * the sibling products' copy, and handles markets the same way — a message classified to the
 * region, or to no market at all, applies in a market under it. One cache entry per brand, so
 * switching product, re-scoping and filtering cost no request.
 */
export const useProductMessages = (brandCode: string | null) =>
	useQuery({
		queryKey: ['product-messages', brandCode],
		queryFn: async (): Promise<MessageWithTags[]> => {
			const refs = await classificationApi.search(ClassifiedObjectType.MESSAGE, {
				[AxisCode.BRAND]: [brandCode as string],
			})
			return hydrateMessages(refs.map((ref) => ref.objectId))
		},
		enabled: !!brandCode,
	})

/**
 * The brand's message matrix: the copy classified to the brand itself, PLUS the copy of every
 * product under it.
 *
 * Two searches rather than one because `brand` and `product` are separate axes — a filter
 * naming both would mean "classified to the brand AND to a product", and a message tagged only
 * to a product would not match a brand search at all. Read as a union and deduped, so a message
 * carrying both tags is listed once.
 *
 * The product codes are the caller's, not derived here: the brand page already holds its
 * products, and a second read for a list it has would be a request spent on nothing.
 */
export const useBrandMessages = (brandCode: string | null, productCodes: string[]) =>
	useQuery({
		queryKey: ['brand-messages', brandCode, productCodes],
		queryFn: async (): Promise<MessageWithTags[]> => {
			const [brandRefs, productRefs] = await Promise.all([
				classificationApi.search(ClassifiedObjectType.MESSAGE, { [AxisCode.BRAND]: [brandCode as string] }),
				productCodes.length > 0
					? classificationApi.search(ClassifiedObjectType.MESSAGE, { [AxisCode.BRAND]: productCodes })
					: Promise.resolve([]),
			])
			return hydrateMessages([...new Set([...brandRefs, ...productRefs].map((ref) => ref.objectId))])
		},
		enabled: !!brandCode,
	})

/**
 * One logo per product, for a grid of them.
 *
 * ONE discover for every product on the page, not one per card: the endpoint takes a
 * multi-value filter, and a request per tile would put a read behind every card in a grid
 * that exists to be scanned at a glance.
 *
 * `applies_in` rather than plain filing, so the logo returned for a market is the one that
 * COVERS it — a logo tagged `emea` applies in Spain, one tagged `emea_fr` does not. Where a
 * product has several, the first wins; the page holds no ladder to choose between regional
 * wordmarks, and the brand hero already resolves its own the same way.
 */
export const useProductLogos = (productCodes: string[], market: string | null) => {
	// Sorted and joined: the array identity changes every render, and an unsorted key would
	// miss the cache whenever the grid reorders.
	const key = [...productCodes].sort().join(',')
	return useQuery({
		queryKey: ['product-logos', key, market],
		queryFn: async (): Promise<Record<string, FileWithTagsDTO>> => {
			const files = await fileService.discover(
				{
					[AxisCode.BRAND]: productCodes,
					[AxisCode.FILE_TYPE]: [FileType.LOGO],
					...(market ? { [AxisCode.GEOGRAPHY]: [market] } : {}),
				},
				{},
				// One logo per product is what the grid renders; the cap bounds a brand whose
				// products carry many without changing what is shown.
				Math.max(productCodes.length * 4, 50),
				undefined,
				market ? 'applies_in' : undefined,
			)
			const first: Record<string, FileWithTagsDTO> = {}
			for (const file of files) {
				for (const code of productCodedOn(file, productCodes)) {
					if (!first[code]) first[code] = file
				}
			}
			return first
		},
		enabled: productCodes.length > 0,
	})
}

/** Which of the requested products a discovered file is classified to. */
function productCodedOn(file: FileWithTagsDTO, wanted: string[]): string[] {
	const codes = new Set(wanted)
	return (file.tags ?? [])
		.filter((t) => t.axisCode === AxisCode.BRAND && codes.has(t.valueCode))
		.map((t) => t.valueCode)
}
